import * as React from "react";
import { ContextMenu as ContextMenuPrimitive } from "radix-ui";
import { useNavigate } from "./router";
import { cn } from "./utils";
import { Elevated } from "./elevated";
import { COUNT_BUBBLE } from "./count";

export type ContextMenuItem =
  | {
      type?: "item";
      label: string;
      onSelect?: () => void;
      href?: string;
      newTab?: boolean;
      icon?: React.ReactNode;
      destructive?: boolean;
      disabled?: boolean;
      shortcut?: string;
      /** Marks the current selection. Rendered with a tinted background +
       *  foreground text (the selected surface) rather than a check icon. */
      selected?: boolean;
      /** Optional trailing count badge — a small primary attention pill on the
       *  right (the same bubble the user-menu glyph carries), so a row can
       *  advertise its own unread tally. Hidden when 0 / undefined. */
      count?: number;
    }
  | { type: "separator" }
  | { type: "label"; label: string };

interface ContextMenuProps {
  items: ContextMenuItem[];
  children: React.ReactNode;
  /** When true (default), the trigger renders as a wrapper around `children`
   *  via Radix's `asChild`, so callers don't get an extra div. */
  asChild?: boolean;
  /** Width hint for the menu in px. Default 200. */
  widthPx?: number;
}

/**
 * Right-click context menu. Pass an `items` array; the component handles
 * navigation (`href`), separators, and labels. Built on
 * `radix-ui` ContextMenu so keyboard nav, focus, and dismissal Just Work.
 */
export function ContextMenu({
  items,
  children,
  asChild = true,
  widthPx = 200,
}: ContextMenuProps) {
  const navigate = useNavigate();

  return (
    <ContextMenuPrimitive.Root>
      <ContextMenuPrimitive.Trigger asChild={asChild}>
        {children}
      </ContextMenuPrimitive.Trigger>
      <ContextMenuPrimitive.Portal>
        <Elevated offset={2} shadowLevel={3} asChild>
        <ContextMenuPrimitive.Content
          className={cn(
            // z-[90]: above Dialog/Sheet (z-[80]); see packages/ui/src/dialog.tsx z-ledger.
            "z-[90] overflow-hidden rounded-md p-1 text-foreground",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
          )}
          style={{ minWidth: widthPx }}
        >
          {items.map((item, i) => {
            if ("type" in item && item.type === "separator") {
              return (
                <ContextMenuPrimitive.Separator
                  key={`sep-${i}`}
                  className="my-1 h-px bg-border"
                />
              );
            }
            if ("type" in item && item.type === "label") {
              return (
                <ContextMenuPrimitive.Label
                  key={`lab-${i}-${item.label}`}
                  className="px-2 py-1.5 type-body text-muted-foreground"
                >
                  {item.label}
                </ContextMenuPrimitive.Label>
              );
            }
            return (
              <ContextMenuPrimitive.Item
                key={`it-${i}-${item.label}`}
                disabled={item.disabled}
                onSelect={(e) => {
                  if (item.disabled) {
                    e.preventDefault();
                    return;
                  }
                  if (item.href) {
                    if (item.newTab) {
                      window.open(item.href, "_blank", "noopener,noreferrer");
                    } else {
                      navigate.push(item.href);
                    }
                    return;
                  }
                  item.onSelect?.();
                }}
                className={cn(
                  "relative flex cursor-default select-none items-center gap-2 rounded px-2 py-1.5 type-body",
                  "outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus-visible:ring-0",
                  "data-[highlighted]:bg-foreground/[0.06] data-[highlighted]:text-foreground",
                  "data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
                  item.selected && "bg-selected font-medium text-foreground",
                  item.destructive && "text-destructive data-[highlighted]:text-destructive",
                )}
              >
                {item.icon ? (
                  <span className="inline-flex h-3.5 w-3.5 shrink-0 items-center justify-center">
                    {item.icon}
                  </span>
                ) : null}
                <span className="flex-1 truncate">{item.label}</span>
                {item.shortcut ? (
                  <span className="ml-auto type-body tracking-widest text-muted-foreground">
                    {item.shortcut}
                  </span>
                ) : null}
                {typeof item.count === "number" && item.count > 0 ? (
                  <span className={cn(COUNT_BUBBLE, "ml-auto shrink-0 type-subtext")}>
                    {item.count > 99 ? "99+" : item.count}
                  </span>
                ) : null}
              </ContextMenuPrimitive.Item>
            );
          })}
        </ContextMenuPrimitive.Content>
        </Elevated>
      </ContextMenuPrimitive.Portal>
    </ContextMenuPrimitive.Root>
  );
}
